<template>
  <div class="update-step">
    <h3>
      <span
        v-if="selectedTierPlan.id === currentTierPlan.id"
        v-html="$t('subscription.already_has_plan')"
      ></span>
      <span v-else>
        {{ $t('sub_update.account_will') }}
        <span
          class="anim-underline anim-underline_small"
          :class="`${
            selectedPlan.tier === 'STARTER' ? 'anim-underline_blue' : 'anim-underline_gold'
          }`"
        >
          <span
            class="anim-underline_text"
            v-html="downgrading ? $t('sub_update.downgrade') : $t('sub_update.upgrade')"
          >
          </span>
        </span>
        to:
      </span>
    </h3>

    <div class="update-step_step_title d-flex mt-2">
      <TierTag
        data-tst="new_plan"
        :customGame="selectedPlan.type"
        :customTier="selectedPlan.tier"
        :paymentInterval="selectedPlan.interval"
      />
    </div>

    <div
      v-if="selectedTierPlan.yearlySavings"
      class="weight_500 mt-2"
    >
      {{
        $t('subscription.want_to_save_amount_year_tra01', {
          amount_num: numFixed(selectedTierPlan.yearlySavings),
        })
      }}
      <span
        class="switch-to-yearly weight_700"
        @click="switchToYearly"
      >
        {{ $t('subscription.switch_to_yearly') }}
      </span>
    </div>
    <div
      v-if="currentPlan.tier === 'PREMIUM' && selectedPlan.tier === 'STARTER'"
      class="mt-4 pt-4 weight_500 bt-grey"
    >
      <div class="d-flex clr-analyze weight_500">
        <v-icon
          class="tier-tag_icon"
          color="#ffd54f"
          >mdi-alert-circle-outline</v-icon
        >
        {{ $t('payment.downgrade_to_starter_are_you_sure') }}
      </div>
      <ul class="mt-1">
        <li class="ml-2">{{ $t(`payment.aggregation_reports`) }}</li>
        <li class="ml-2">{{ $t(`payment.weekly_coaching`) }}</li>
        <li class="ml-2">
          {{
            $t(`payment.only_50`, {
              premium_hands_mo: (100000).toLocaleString($i18n.locale),
            })
          }}
        </li>
      </ul>
    </div>
    <!-- {{ currentTierPlan.id !== selectedTierPlan.id }} -->

    <!-- {{ currentTierPlan.id }} -->
    <!-- {{ selectedTierPlan.id }} -->
    <div
      v-if="currentTierPlan?.id !== selectedTierPlan?.id"
      class="f-column mt-4 bt-grey"
    >
      <div
        class="transition_04"
        :class="{ 'is-blurred': loading }"
        data-tst="update_plan_text"
      >
        <div
          v-if="upgrading && !manualCheckout"
          class="mt-4 upgrading"
        >
          <span v-if="currentPlan.provider === 'Paypal'">
            <span
              v-html="
                $t('payment.you_will_get_access_paypal', {
                  new_tier: selectedPayPlanFullName,
                  subscription_expiration: currentSubExpirationDate,
                  current_tier: currentTierPlanFullName,
                })
              "
            >
            </span>
            &nbsp;
            <OpenChatLink />.
          </span>
          <span
            v-if="currentPlan.provider === 'Stripe'"
            v-html="
              $t('payment.you_will_get_access_instantly_stripe', {
                extra_amount: `${numFixed(Math.abs(upgradePrice), 2)} USD`,
                current_tier: currentTierPlanFullName,
                new_tier: selectedPayPlanFullName,
              })
            "
          >
          </span>
          <span
            v-if="isCurrentTierPlanFree"
            v-html="
              $t('payment.you_will_get_access_instantly_free', {
                amount: `${numFixed(Math.abs(upgradePrice), 2)} USD`,
                current_tier: currentTierPlanFullName,
                new_tier: selectedPayPlanFullName,
              })
            "
          />
          <span
            v-else-if="currentPlan.provider === 'Adyen'"
            :data-tst="`upgrade_price_${upgradePrice ? upgradePrice.toFixed() : ''}`"
            v-html="
              $t('payment.you_will_get_access_instantly_stripe', {
                extra_amount: `${numFixed(Math.abs(upgradePrice), 2)} USD`,
                current_tier: currentTierPlanFullName,
                new_tier: selectedPayPlanFullName,
              })
            "
          />
        </div>
        <div
          v-if="manualCheckout"
          class="mt-4 downgrading"
          data-tst="manual_checkout"
        >
          <div
            v-html="
              $t(`payment.manual_checkout_tra01`, {
                new_tier: selectedPayPlanFullName,
                mailto: 'mailto:hello@gtowizard.com',
                email: 'hello@gtowizard.com',
              })
            "
          />
        </div>
        <div
          v-else-if="downgrading"
          class="mt-4 downgrading"
          v-html="
            $t('payment.downgrade_to_tier', {
              current_tier: currentTierPlanFullName,
              new_tier: selectedPayPlanFullName,
              subscription_expiration: currentSubExpirationDate,
            })
          "
        ></div>
        <div
          v-else
          class="subcription-text mt-4"
          v-html="
            !isCurrentPlanRecurring
              ? $t('payment.sub_no_renew', {
                  date: currentSubExpirationDate,
                })
              : selectedPlan.interval === 'MONTHLY'
                ? $t('payment.sub_renew_month', {
                    date: currentSubExpirationDate,
                  })
                : $t('payment.sub_renew_year', {
                    date: currentSubExpirationDate,
                  })
          "
        />
      </div>
    </div>
  </div>
</template>

<script>
import format_mix from '@/mixins/format_mix'
import TierTag from '@/components/Sub/TierTag'
import payment_mix from '@/mixins/payment_mix'
import links_mix from '@/mixins/links_mix'
import OpenChatLink from '@/components/Payments/OpenChatLink.vue'

export default {
  name: 'UpdatePlanCheckout',
  components: {
    OpenChatLink,
    TierTag,
  },
  mixins: [format_mix, payment_mix, links_mix],
  props: {
    loading: Boolean,
    upgradePrice: Number,
    manualCheckout: Boolean,
  },
  emits: ['switch-to-yearly'],

  methods: {
    switchToYearly() {
      this.$emit('switch-to-yearly')
    },
  },
  computed: {
    currentSubExpirationDate() {
      if (this.currentSub.expiration_date) {
        return this.formatISODate(this.currentSub.expiration_date)
      }
      return null
    },
    currentTierPlanFullName() {
      return `${this.$t(`gametypes.groups.${this.currentPlan.type.toLowerCase()}_long`)} / ${
        this.currentTierPlan.full_name
      }`
    },
    selectedPayPlanFullName() {
      return `${this.$t(`gametypes.groups.${this.selectedPlan.type.toLowerCase()}_long`)} / ${
        this.selectedTierPlan.full_name
      }`
    },
    downgrading() {
      return this.currentTierPlan.rank > this.selectedTierPlan.rank
    },
    upgrading() {
      return !this.downgrading
    },
  },
}
</script>

<style lang="scss" scoped>
.update-step {
  max-width: 34rem;
  width: 100%;
  &_step {
    &_title {
      font-size: 1.5rem;
      font-weight: 700;
      white-space: nowrap;
    }
    &_sub {
      display: flex;
      font-size: 1rem;
    }
  }
}
.update-steps {
  font-size: 0.8rem;
  color: var(--clr-grey-2);
  padding: 0 1rem;
}
.switch-to-yearly {
  cursor: pointer;
  color: var(--clr-primary);
  &:hover {
    text-decoration: underline;
  }
}

.tier-tag {
  border-radius: 4px;
  font-weight: 700;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  flex: 0;
  &_trial {
    color: var(--clr-white-0);
  }
  &_starter {
    color: var(--clr-blue);
  }
  &_premium {
    color: var(--clr-yellow);
  }
  &_icon {
    font-size: 1.5rem;
    margin: 0 0.5rem 0 0;
    color: currentColor;
  }
}
</style>
